// primitives.jsx — small stateless presentational atoms shared across the Clients module.
import React, { useState, useEffect } from "react";
import { fmtMoney } from "../utils";
import { G, GL, GR } from "./constants";

export const Ico = ({ d, sz = 14, col = "currentColor", cls = "" }) => (
  <svg
    width={sz}
    height={sz}
    viewBox="0 0 24 24"
    fill="none"
    stroke={col}
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cls}
  >
    <path d={d} />
  </svg>
);

export const KV = ({ label, value, wide, mono }) => (
  <div className={`flex flex-col gap-0.5 ${wide ? "col-span-2" : ""}`}>
    <span className="text-[9px] font-black uppercase tracking-[0.14em] text-slate-400 select-none">
      {label}
    </span>
    <span
      className={`text-sm font-medium text-slate-700 leading-snug ${mono ? "font-mono" : ""}`}
    >
      {value ?? <span className="text-slate-300">—</span>}
    </span>
  </div>
);

export const Divider = ({ label }) => (
  <div className="flex items-center gap-3 py-1">
    <div className="flex-1 h-px bg-slate-100" />
    {label && (
      <span className="text-[9px] font-black uppercase tracking-[0.18em] text-slate-300 whitespace-nowrap">
        {label}
      </span>
    )}
    <div className="flex-1 h-px bg-slate-100" />
  </div>
);

export const PayBar = ({ paid = 0, total = 0, showLabels = true, currency = "USD" }) => {
  if (!total) return null;
  const pct = Math.min(100, (paid / total) * 100);
  const bg = pct >= 100 ? "#10b981" : pct >= 50 ? G : "#f43f5e";
  return (
    <div>
      {showLabels && (
        <div className="flex justify-between text-[10px] text-slate-400 mb-1">
          <span>{fmtMoney(paid, currency)} paid</span>
          <span className="font-bold" style={{ color: bg }}>
            {Math.round(pct)}%
          </span>
          <span>{fmtMoney(total, currency)} total</span>
        </div>
      )}
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: bg }}
        />
      </div>
    </div>
  );
};

export const MoneyRow = ({ cost, deposit, paid, currency = "USD" }) => (
  <div className="grid grid-cols-3 gap-2 mt-3">
    {[
      ["Cost", cost, "text-slate-600"],
      ["Deposit", deposit, "text-amber-600"],
      ["Paid", paid, "text-emerald-600"],
    ].map(([l, v, c]) => (
      <div
        key={l}
        className="bg-slate-50 rounded-xl py-2.5 text-center border border-slate-100"
      >
        <p className={`text-xs font-black ${c}`}>{fmtMoney(v, currency)}</p>
        <p className="text-[9px] uppercase font-black tracking-widest text-slate-400 mt-0.5">
          {l}
        </p>
      </div>
    ))}
  </div>
);

export const StatusPill = ({ label, colorClass }) => (
  <span
    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider capitalize ${colorClass}`}
  >
    {label?.replace(/_/g, " ")}
  </span>
);

export const PriorityDot = ({ p }) => (
  <span
    className={`w-2 h-2 rounded-full flex-shrink-0 inline-block ${p === "high" ? "bg-rose-500" : p === "medium" ? "bg-amber-400" : "bg-emerald-400"}`}
  />
);

export const EmptyTabState = ({ icon, label }) => (
  <div className="flex flex-col items-center justify-center py-14 text-center">
    <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
      <Ico d={icon} sz={26} col="#cbd5e1" />
    </div>
    <p className="text-sm font-bold text-slate-400">{label}</p>
  </div>
);


// ─── Document preview + viewer ───────────────────────────────────────────────
// Uploaded URLs carry the original filename after "#", e.g. ".../x7k2p.pdf#Passport - Ada.pdf".
// The fragment never reaches the server, so the link is unchanged — it's just a label.
export const docName = (u) => {
  const s = String(u || "");
  try {
    const hash = s.includes("#") ? decodeURIComponent(s.slice(s.indexOf("#") + 1)) : "";
    if (hash) return hash;
    return decodeURIComponent(s.split("#")[0].split("/").pop().split("?")[0]) || "document";
  } catch { return "document"; }
};

/** Attach the original filename to an uploaded URL so it can be identified later. */
export const withDocName = (url, filename) => {
  const name = String(filename || "").trim();
  return name ? `${String(url).split("#")[0]}#${encodeURIComponent(name)}` : url;
};

const IMG_EXT = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "avif"];
// File type comes from the real URL path (before any "#name" label or "?query").
const extOf = (u) => (String(u).split("#")[0].split("?")[0].split(".").pop() || "").toLowerCase();

// Full-screen in-app preview. Renders images inline, PDFs in an iframe, and offers
// an "open in new tab" fallback for anything else. Supports prev/next + keyboard.
export const DocPreviewModal = ({ docs = [], index = 0, onClose }) => {
  const list = (docs || []).filter(Boolean);
  const [i, setI] = useState(index);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setI((v) => Math.min(list.length - 1, v + 1));
      if (e.key === "ArrowLeft")  setI((v) => Math.max(0, v - 1));
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [list.length, onClose]);

  if (!list.length) return null;
  const safe = Math.max(0, Math.min(i, list.length - 1));
  const url = list[safe];
  const ext = extOf(url);
  const isImg = IMG_EXT.includes(ext);
  const isPdf = ext === "pdf";

  return (
    <div
      className="fixed inset-0 z-[210] flex items-center justify-center p-4"
      style={{ background: "rgba(10,15,30,0.8)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden"
        style={{ animation: "modalIn .2s ease" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 flex-shrink-0">
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-800 truncate">{docName(url)}</p>
            {list.length > 1 && <p className="text-[11px] text-slate-400">{safe + 1} of {list.length}</p>}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <a
              href={url} target="_blank" rel="noopener noreferrer"
              className="text-[11px] font-bold px-2.5 py-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"
              title="Open in new tab"
            >
              Open ↗
            </a>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
            >
              <Ico d="M6 18L18 6M6 6l12 12" sz={14} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-slate-50 flex items-center justify-center p-3 relative min-h-[240px]">
          {isImg ? (
            <img src={url} alt={docName(url)} className="max-w-full max-h-[72vh] object-contain rounded-lg" />
          ) : isPdf ? (
            <iframe title={docName(url)} src={url} className="w-full h-[72vh] rounded-lg border-0 bg-white" />
          ) : (
            <div className="text-center py-16 px-6">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Ico d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" sz={26} col="#94a3b8" />
              </div>
              <p className="text-sm text-slate-500 mb-3">This file type can't be previewed here.</p>
              <a
                href={url} target="_blank" rel="noopener noreferrer"
                className="inline-block text-xs font-bold px-3 py-2 rounded-lg"
                style={{ color: G, background: GL }}
              >
                Open in new tab ↗
              </a>
            </div>
          )}

          {list.length > 1 && (
            <>
              <button
                onClick={() => setI((v) => Math.max(0, v - 1))}
                disabled={safe === 0}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow flex items-center justify-center text-slate-500 hover:text-slate-700 disabled:opacity-30 transition-all"
                aria-label="Previous"
              >
                <Ico d="M15 19l-7-7 7-7" sz={16} />
              </button>
              <button
                onClick={() => setI((v) => Math.min(list.length - 1, v + 1))}
                disabled={safe === list.length - 1}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white shadow flex items-center justify-center text-slate-500 hover:text-slate-700 disabled:opacity-30 transition-all"
                aria-label="Next"
              >
                <Ico d="M9 5l7 7-7 7" sz={16} />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Read-only chips that open the in-app preview (no routing out). Null when empty.
export const DocList = ({ docs, label = "Uploaded Documents" }) => {
  const list = (docs || []).filter(Boolean);
  const [preview, setPreview] = useState(null);
  if (!list.length) return null;
  return (
    <div>
      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {list.map((u, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setPreview(i)}
            className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-lg border transition-colors hover:shadow-sm"
            style={{ color: G, borderColor: GR, background: GL }}
          >
            <Ico d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6m-6 4h4" sz={11} col={G} />
            <span className="max-w-[150px] truncate">{docName(u)}</span>
          </button>
        ))}
      </div>
      {preview !== null && (
        <DocPreviewModal docs={list} index={preview} onClose={() => setPreview(null)} />
      )}
    </div>
  );
};
